
// services/bandi.service.ts
import { Injectable, Injector } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { lastValueFrom, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { JsonDataService } from './json-data.service';
import { PlatformApiWPublicService } from './platform-apiwp-public.service';
import { PlatformApiWpService } from './platform-apiwp.service';
import { TokenService } from './token.service';
import { MareAuthService } from '@mare-common/ngx-mare-auth';
import { DoubleService } from './double-service';

export interface Sector {
  id: number;
  title: string;
  key: string;
}

export interface Beneficiary {
  id: number;
  title: string;
  key: string;
}

export interface Bando {
  id: number;
  title: string;
  deadline_label: string;
  deadline_days_left: number;
  published: string;
  budget: number;
  status: number;
  url: string;
  sectors: Sector[];
  beneficiaries: Beneficiary[];
  section_program: string;
  is_recent: boolean;
}

export interface BandiResponse {
  status: string;
  payload: {
    count: number;
    next: string | null;
    previous: string | null;
    results: Bando[];
  };
}

export interface FilterOption {
  id: number;
  title: string;
  key?: string;
}

export interface BandiFilters {
  sectors?: number[];
  beneficiaries?: number[];
  types?: number[];
  regions?: number[];
  programs?: number[];
  status?: number;
  search?: string;
  ordering?: string;
  page?: number;
}

@Injectable({
  providedIn: 'root'
})
export class ObiettivoEuropaService extends DoubleService {

  /**
   * platformApiWpService viene inizializzato con PlatformApiWPublicService se non sono loggati
   * altrimenti viene inizializzato con PlatformApiWpService
   * PlatformApiWPublicService si occupa di prendere il token dalla query string e inserirlo in query string su tutte le GET
   */
  // private platformApiWpService!: PlatformApiWPublicService | PlatformApiWpService;
  getBandiSub: Subject<BandiResponse> = new Subject<BandiResponse>();
  configs: any;
  isInit: boolean = false;
  isInit$ = new Subject<boolean>();
  constructor(
    private jsonDataService: JsonDataService,
    protected override injector: Injector,
    protected override tokenService: TokenService,
    protected override authService: MareAuthService
  ) {
    super(authService, tokenService, injector );
    if (this.tokenService.isInit) {
      this.init();
    } else {
      let subscription = this.tokenService.isInit$.subscribe((res) => {
        if (res) {
          subscription.unsubscribe();
          this.init();
        }
      })
    }
  }

  async init() {
    if (this.authService.isLogged()) {
      this.platformApiWpService = this.injector.get(PlatformApiWpService);
    } else {
      this.platformApiWpService = this.injector.get(PlatformApiWPublicService);
    }
    await this.getConfig();
    this.isInit = true;
    this.isInit$.next(true);
  }

  mockData = {
    "status": "ok",
    "payload": {
      "count": 1087,
      "next": "https://www.obiettivoeuropa.com/api/call/?ordering=&page=2",
      "previous": null,
      "results": [
        {
          "id": 18520,
          "deadline_label": "16 dicembre 2024",
          "deadline_days_left": 60,
          "published": "2024-10-18",
          "title": "Abruzzo - Bando Intervento SRG03 Partecipazione a regimi di qualità 2024",
          "budget": 300000,
          "status": 1,
          "url": "/bandi/abruzzo-bando-intervento-srg03-partecipazione-a-regimi-di-qualita",
          "sectors": [
            {
              "id": 21,
              "title": "Agricoltura e sviluppo rurale",
              "key": "sector-21"
            },
            {
              "id": 29,
              "title": "Supporto alle imprese",
              "key": "sector-29"
            },
            {
              "id": 28,
              "title": "Sviluppo e promozione territoriale",
              "key": "sector-28"
            }
          ],
          "beneficiaries": [
            {
              "id": 62,
              "title": "Imprese",
              "key": "beneficiary-62"
            },
            {
              "id": 67,
              "title": "Imprese sociali/Società benefit",
              "key": "beneficiary-67"
            },
            {
              "id": 71,
              "title": "Liberi professionisti",
              "key": "beneficiary-71"
            }
          ],
          "programs": [],
          "types": [
            {
              "id": 3,
              "title": "Bandi regionali / locali",
              "key": "type-3"
            }
          ],
          "regions": [
            {
              "id": 13,
              "name": "Abruzzo",
              "title": "Abruzzo",
              "key": "region-13"
            }
          ],
          "section_program": "<p>Regione Abruzzo</p>",
          "is_recent": true,
          "pnrr": false
        },
        {
          "id": 21360,
          "deadline_label": "31 maggio 2025",
          "deadline_days_left": 226,
          "published": "2024-10-18",
          "title": "GAL Giarolo Leader – Bando SRE04: Start-up non agricole",
          "budget": 280000,
          "status": 1,
          "url": "/bandi/gal-giarolo-leader-bando-sre04-start-up-non-agricole",
          "sectors": [
            {
              "id": 21,
              "title": "Agricoltura e sviluppo rurale",
              "key": "sector-21"
            },
            {
              "id": 24,
              "title": "Ambiente e Sviluppo sostenibile",
              "key": "sector-24"
            },
            {
              "id": 26,
              "title": "Arte e Cultura",
              "key": "sector-26"
            },
            {
              "id": 106,
              "title": "Artigianato",
              "key": "sector-106"
            },
            {
              "id": 92,
              "title": "Commercio",
              "key": "sector-92"
            },
            {
              "id": 101,
              "title": "Competitività imprese",
              "key": "sector-101"
            },
            {
              "id": 84,
              "title": "Economia circolare",
              "key": "sector-84"
            },
            {
              "id": 107,
              "title": "Green economy",
              "key": "sector-107"
            },
            {
              "id": 34,
              "title": "Innovazione tecnologica, digitalizzazione, ICT",
              "key": "sector-34"
            },
            {
              "id": 95,
              "title": "Manifatturiero",
              "key": "sector-95"
            },
            {
              "id": 29,
              "title": "Supporto alle imprese",
              "key": "sector-29"
            },
            {
              "id": 28,
              "title": "Sviluppo e promozione territoriale",
              "key": "sector-28"
            },
            {
              "id": 42,
              "title": "Turismo",
              "key": "sector-42"
            }
          ],
          "beneficiaries": [
            {
              "id": 62,
              "title": "Imprese",
              "key": "beneficiary-62"
            },
            {
              "id": 71,
              "title": "Liberi professionisti",
              "key": "beneficiary-71"
            },
            {
              "id": 84,
              "title": "Micro-imprese",
              "key": "beneficiary-84"
            },
            {
              "id": 63,
              "title": "Persone fisiche/Gruppi informali",
              "key": "beneficiary-63"
            },
            {
              "id": 77,
              "title": "Startup",
              "key": "beneficiary-77"
            }
          ],
          "programs": [],
          "types": [
            {
              "id": 3,
              "title": "Bandi regionali / locali",
              "key": "type-3"
            }
          ],
          "regions": [
            {
              "id": 1,
              "name": "Piemonte",
              "title": "Piemonte",
              "key": "region-1"
            }
          ],
          "section_program": "<p>GAL Giarolo Leader</p>",
          "is_recent": true,
          "pnrr": false
        },
        {
          "id": 21358,
          "deadline_label": "21 novembre 2024",
          "deadline_days_left": 35,
          "published": "2024-10-18",
          "title": "Abruzzo - Bando di concorso per la XXVII edizione del Premio Internazionale Ignazio Silone. Bando di concorso riservato alle scuole secondarie di II grado italiane o straniere",
          "budget": 0,
          "status": 1,
          "url": "/bandi/abruzzo-bando-di-concorso-per-la-xxvii-edizione-del-premio-internazionale-ignazio-silone-bando-di-concorso-riservato-alle-scuole-secondarie-di-ii-grado-italiane-o-straniere",
          "sectors": [
            {
              "id": 26,
              "title": "Arte e Cultura",
              "key": "sector-26"
            },
            {
              "id": 40,
              "title": "Educazione e istruzione",
              "key": "sector-40"
            }
          ],
          "beneficiaries": [
            {
              "id": 63,
              "title": "Persone fisiche/Gruppi informali",
              "key": "beneficiary-63"
            }
          ],
          "programs": [],
          "types": [
            {
              "id": 3,
              "title": "Bandi regionali / locali",
              "key": "type-3"
            }
          ],
          "regions": [
            {
              "id": 13,
              "name": "Abruzzo",
              "title": "Abruzzo",
              "key": "region-13"
            }
          ],
          "section_program": "<p>Regione Abruzzo</p>",
          "is_recent": true,
          "pnrr": false
        },
        {
          "id": 21359,
          "deadline_label": "14 marzo 2025",
          "deadline_days_left": 148,
          "published": "2024-10-18",
          "title": "GAL Mongioie – Bando SRE04: Start-up non agricole",
          "budget": 150000,
          "status": 1,
          "url": "/bandi/gal-mongioie-bando-sre04-start-up-non-agricole",
          "sectors": [
            {
              "id": 21,
              "title": "Agricoltura e sviluppo rurale",
              "key": "sector-21"
            },
            {
              "id": 24,
              "title": "Ambiente e Sviluppo sostenibile",
              "key": "sector-24"
            },
            {
              "id": 26,
              "title": "Arte e Cultura",
              "key": "sector-26"
            },
            {
              "id": 106,
              "title": "Artigianato",
              "key": "sector-106"
            },
            {
              "id": 92,
              "title": "Commercio",
              "key": "sector-92"
            },
            {
              "id": 101,
              "title": "Competitività imprese",
              "key": "sector-101"
            },
            {
              "id": 84,
              "title": "Economia circolare",
              "key": "sector-84"
            },
            {
              "id": 45,
              "title": "Energie Rinnovabili",
              "key": "sector-45"
            },
            {
              "id": 107,
              "title": "Green economy",
              "key": "sector-107"
            },
            {
              "id": 34,
              "title": "Innovazione tecnologica, digitalizzazione, ICT",
              "key": "sector-34"
            },
            {
              "id": 95,
              "title": "Manifatturiero",
              "key": "sector-95"
            },
            {
              "id": 32,
              "title": "Servizi sociali e socio-sanitari",
              "key": "sector-32"
            },
            {
              "id": 29,
              "title": "Supporto alle imprese",
              "key": "sector-29"
            },
            {
              "id": 28,
              "title": "Sviluppo e promozione territoriale",
              "key": "sector-28"
            },
            {
              "id": 42,
              "title": "Turismo",
              "key": "sector-42"
            }
          ],
          "beneficiaries": [
            {
              "id": 62,
              "title": "Imprese",
              "key": "beneficiary-62"
            },
            {
              "id": 71,
              "title": "Liberi professionisti",
              "key": "beneficiary-71"
            },
            {
              "id": 84,
              "title": "Micro-imprese",
              "key": "beneficiary-84"
            },
            {
              "id": 63,
              "title": "Persone fisiche/Gruppi informali",
              "key": "beneficiary-63"
            }
          ],
          "programs": [],
          "types": [
            {
              "id": 3,
              "title": "Bandi regionali / locali",
              "key": "type-3"
            }
          ],
          "regions": [
            {
              "id": 1,
              "name": "Piemonte",
              "title": "Piemonte",
              "key": "region-1"
            }
          ],
          "section_program": "<p>GAL Mongioie</p>",
          "is_recent": true,
          "pnrr": false
        },
        {
          "id": 21361,
          "deadline_label": "22 novembre 2024",
          "deadline_days_left": 36,
          "published": "2024-10-18",
          "title": "Friuli - Venezia Giulia - Contributi per l’adeguamento e l’allestimento dei locali o spazi idonei da adibire all’allattamento e all’igiene dei neonati",
          "budget": 200000,
          "status": 2,
          "url": "/bandi/friuli-venezia-giulia-contributi-per-ladeguamento-e-lallestimento-dei-locali-o-spazi-idonei-da-adibire-allallattamento-e-alligiene-dei-neonati",
          "sectors": [
            {
              "id": 27,
              "title": "Diritti e Cittadinanza",
              "key": "sector-27"
            },
            {
              "id": 93,
              "title": "Servizi",
              "key": "sector-93"
            }
          ],
          "beneficiaries": [
            {
              "id": 69,
              "title": "Enti pubblici",
              "key": "beneficiary-69"
            }
          ],
          "programs": [],
          "types": [
            {
              "id": 3,
              "title": "Bandi regionali / locali",
              "key": "type-3"
            }
          ],
          "regions": [
            {
              "id": 6,
              "name": "Friuli-Venezia Giulia",
              "title": "Friuli-Venezia Giulia",
              "key": "region-6"
            }
          ],
          "section_program": "<p>Regione Friuli - Venezia Giulia</p>",
          "is_recent": true,
          "pnrr": false
        }
      ]
    }
  };

  // getBandi(page: number = 1): Observable<BandiResponse> {
  //   // const params = { page: page.toString() };
  //   // return this.http.get<BandiResponse>(this.apiUrl, { params });
  //   return of(this.mockData);
  // }

  getBandi(filters: BandiFilters): Observable<BandiResponse> {
    let params = new HttpParams();

    if (filters.page) {
      params = params.set('page', filters.page.toString());
    }

    if (filters.ordering) {
      params = params.set('ordering', filters.ordering);
    }

    if (filters.search) {
      params = params.set('search', filters.search);
    }

    if (filters.status) {
      params = params.set('status', filters.status.toString());
    }

    // Handle array parameters
    ['sectors', 'beneficiaries', 'types', 'regions', 'programs'].forEach((key) => {
      const value = filters[key as keyof BandiFilters];
      if (value && Array.isArray(value) && value.length > 0) {
        params = params.set(key, value.join(','));
      }
    });

    this.platformApiWpService.getRequest('msc/v1/retrieveBandi', {params}).then((sub) => {
      sub
        .pipe(
          catchError((err) => {
            console.error(err);
            this.getBandiSub.error(err);
            return throwError(() => err);
          })
        )
        .subscribe((res: any) => {
          this.getBandiSub.next(res);
        })
    })
    return this.getBandiSub;
    // return this.http.get<BandiResponse>(this.apiUrl, { params });
  }

  // getSectors(): Observable<FilterOption[]> {
  //   if (!this.isInit) {
  //     let sub = new Subject<FilterOption[]>();
  //     this.isInit$.subscribe(
  //       () => {
  //         return sub.next(this.configs.sectors)
  //       }
  //     );
  //     return sub;
  //   }
  //   return of(this.configs.sectors);
  //   // return this.http.get<FilterOption[]>(`${this.apiUrl}/sectors`);
  // }

  getSectors(): Observable<FilterOption[]> {
    if (!this.isInit) {
      return this.isInit$.pipe(switchMap(() => of(this.configs.sectors)));
    }
    return of(this.configs.sectors);
    // return this.http.get<FilterOption[]>(`${this.apiUrl}/beneficiaries`);
  }

  getBeneficiaries(): Observable<FilterOption[]> {
    if (!this.isInit) {
      return this.isInit$.pipe(switchMap(() => of(this.configs.beneficiaries)));
    }
    return of(this.configs.beneficiaries);
    // return this.http.get<FilterOption[]>(`${this.apiUrl}/beneficiaries`);
  }

  getTypes(): Observable<FilterOption[]> {
    if (!this.isInit) {
      return this.isInit$.pipe(switchMap(() => of(this.configs.types)));
    }
    return of(this.configs.types);
    // return this.http.get<FilterOption[]>(`${this.apiUrl}/types`);
  }

  getRegions(): Observable<FilterOption[]> {
    if (!this.isInit) {
      return this.isInit$.pipe(switchMap(() => of(this.configs.regions)));
    }
    return of(this.configs.regions);
    // return this.http.get<FilterOption[]>(`${this.apiUrl}/regions`);
  }

  getPrograms(): Observable<FilterOption[]> {
    if (!this.isInit) {
      return this.isInit$.pipe(switchMap(() => of(this.configs.programs)));
    }
    return of(this.configs.programs);
    // return this.http.get<FilterOption[]>(`${this.apiUrl}/programs`);
  }

  toggleFavorite(bandoId: number): Observable<any> {
    return of({});
    // Implement your favorite toggle logic here
    // This is just a placeholder implementation
    // return this.http.post(`${this.apiUrl}/favorites/${bandoId}/toggle`, {});
  }

  getConfig(): any {
    return new Promise((resolve, reject) => {
      this.jsonDataService.getJsonData('oe-config.json', 'configs').pipe(
        catchError(err => {
          console.log(err);
          return throwError(() => 'Errore nella lettura delle configurazione');
        })).subscribe(data => {
          this.configs = data;
          resolve(data);
        });
    })

  }
}